import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { avatarUrl } from "../lib/avatar";
import StrategyCard from "../components/StrategyCard";
import { Search, Megaphone, Sparkles } from "lucide-react";

// Helper: enrich strategies with like_count, liked_by_me, comment_count
function enrich(strategies, userId) {
  return strategies.map((s) => ({
    ...s,
    like_count:    s.strategy_likes?.length || 0,
    comment_count: s.comments?.[0]?.count   || 0,
    liked_by_me:   s.strategy_likes?.some((l) => l.user_id === userId) || false,
  }));
}

async function loadFeed(userId) {
  const [strategiesRes, announcementsRes, topPlayersRes] = await Promise.all([
    supabase
      .from("strategies")
      .select("*, author:profiles!user_id(id,name,avatar_url,participant_id), strategy_likes(user_id), comments(count)")
      .order("created_at", { ascending: false })
      .limit(30),
    supabase.from("announcements").select("*").order("created_at", { ascending: false }).limit(5),
    supabase.from("profiles").select("*").neq("role", "admin").order("engagement_score", { ascending: false }).limit(5),
  ]);
  const enriched = enrich(strategiesRes.data || [], userId);
  const topStrats = [...enriched].sort((a, b) => (b.like_count + b.shares) - (a.like_count + a.shares)).slice(0, 5);
  return {
    feed:           enriched,
    top_strategies: topStrats,
    top_players:    topPlayersRes.data || [],
    announcements:  announcementsRes.data || [],
  };
}

export default function Home() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    loadFeed(user?.id).then(setData);
  }, [user?.id]);

  const onLike = async (s) => {
    if (!user) return;
    const wasLiked = s.liked_by_me;
    // Optimistic update
    const patch = (x) => x.id !== s.id ? x : { ...x, liked_by_me: !wasLiked, like_count: wasLiked ? x.like_count - 1 : x.like_count + 1 };
    setData((prev) => ({ ...prev, feed: prev.feed.map(patch), top_strategies: prev.top_strategies.map(patch) }));
    if (wasLiked) {
      await supabase.from("strategy_likes").delete().eq("strategy_id", s.id).eq("user_id", user.id);
    } else {
      await supabase.from("strategy_likes").insert({ strategy_id: s.id, user_id: user.id });
    }
  };

  const onShare = async (s) => {
    if (!user) return;
    try {
      await supabase.from("strategy_shares").insert({ strategy_id: s.id, user_id: user.id });
      await supabase.from("strategies").update({ shares: (s.shares || 0) + 1 }).eq("id", s.id);
      const patch = (x) => x.id !== s.id ? x : { ...x, shares: (x.shares || 0) + 1 };
      setData((prev) => ({ ...prev, feed: prev.feed.map(patch), top_strategies: prev.top_strategies.map(patch) }));
    } catch (_) {}
    try { await navigator.clipboard.writeText(`${window.location.origin}/s/${s.id}`); } catch (_) {}
  };

  if (!data) return <div className="p-10 font-hand text-3xl">loading the feed…</div>;

  const filtered = q
    ? data.feed.filter((s) =>
        s.title.toLowerCase().includes(q.toLowerCase()) ||
        s.summary.toLowerCase().includes(q.toLowerCase()) ||
        (s.tags || []).some((t) => t.includes(q.toLowerCase().replace("#", "")))
      )
    : data.feed;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="grid lg:grid-cols-12 gap-6">
        {/* MAIN COLUMN */}
        <div className="lg:col-span-8 space-y-6">
          <div className="brutal-card p-5 flex items-center gap-2 flex-wrap" data-testid="search-box">
            <Search size={18} />
            <input
              className="flex-1 outline-none bg-transparent font-mono"
              placeholder="Search strategies, tags, players…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-input"
            />
            <Link to="/strategies/new" className="brutal-btn brutal-btn-yellow text-xs" data-testid="new-strategy-btn">
              + New strategy
            </Link>
          </div>

          <section>
            <h2 className="font-display font-extrabold text-3xl uppercase tracking-tight mb-4 flex items-center gap-3">
              <Sparkles className="text-marker-red" /> Community feed
            </h2>
            <div className="grid gap-4">
              {filtered.length === 0 && (
                <div className="brutal-card p-6 font-hand text-2xl">Nothing matches that yet. Be the first to ship something.</div>
              )}
              {filtered.map((s) => <StrategyCard key={s.id} s={s} onLike={onLike} onShare={onShare} />)}
            </div>
          </section>
        </div>

        {/* SIDEBAR */}
        <aside className="lg:col-span-4 space-y-6">
          {data.announcements?.length > 0 && (
            <section className="brutal-card p-5" data-testid="announcements">
              <div className="flex items-center gap-2 mb-3">
                <Megaphone className="text-marker-red" size={20} />
                <h3 className="font-display font-extrabold text-xl uppercase tracking-tight">Announcements</h3>
              </div>
              <ul className="space-y-3">
                {data.announcements.map((a) => (
                  <li key={a.id} className="border-l-4 border-marker-yellow pl-3">
                    <div className="font-display font-extrabold uppercase tracking-tight">{a.title}</div>
                    <div className="text-sm text-ink/80">{a.body}</div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="brutal-card p-5" data-testid="top-strategies">
            <h3 className="font-display font-extrabold text-xl uppercase tracking-tight mb-3">
              Best <span className="highlight-marker">performing</span>
            </h3>
            <ul className="space-y-3">
              {data.top_strategies.slice(0, 4).map((s, idx) => (
                <li key={s.id} className="flex items-start gap-3">
                  <div className="font-display font-extrabold text-2xl text-marker-red w-7 shrink-0">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <Link to={`/s/${s.id}`} className="flex-1">
                    <div className="font-display font-extrabold uppercase tracking-tight leading-tight">{s.title}</div>
                    <div className="font-mono text-xs text-ink/60">
                      ♥ {s.like_count} · ↗ {s.shares} · @{s.author?.name}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section className="brutal-card p-5" data-testid="top-players">
            <h3 className="font-display font-extrabold text-xl uppercase tracking-tight mb-3">
              Top <span className="highlight-marker">players</span>
            </h3>
            <ul className="space-y-3">
              {data.top_players.slice(0, 5).map((p, idx) => (
                <li key={p.id} className="flex items-center gap-3">
                  <div className="font-display font-extrabold text-2xl text-marker-red w-7 shrink-0">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <img src={avatarUrl(p.avatar_url, p.id)} alt={p.name} className="w-10 h-10 border-2 border-ink blob bg-cream" />
                  <Link to={`/u/${p.id}`} className="flex-1 min-w-0">
                    <div className="font-display font-extrabold uppercase tracking-tight truncate">{p.name}</div>
                    <div className="font-mono text-xs text-ink/60">engagement {p.engagement_score}</div>
                  </Link>
                </li>
              ))}
            </ul>
            <Link to="/leaderboard" className="brutal-btn w-full mt-4 text-xs">See full leaderboard →</Link>
          </section>

          {user && (
            <section className="brutal-card p-5 bg-marker-yellow" data-testid="pass-cta">
              <div className="font-hand text-2xl rotate-[-2deg] mb-1">— flex it —</div>
              <h3 className="font-display font-extrabold text-2xl uppercase tracking-tight">Your Player Pass</h3>
              <p className="text-sm mt-1">Download &amp; share it. People notice.</p>
              <Link to="/pass" className="brutal-btn mt-3 w-full">Open my pass →</Link>
            </section>
          )}
        </aside>
      </div>
    </main>
  );
}
